import { LoaderFunction, replace } from 'react-router-dom';
import debug from 'debug';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';

// Logger
const logLoader = debug('rois:newcommunication:loader');
import { FETCH_PREDEFINED_ORDERS } from '../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import { formNaming } from '../../../../components/forms/NewCommunicationForm/constants';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../gql/queries/orders/predefinedData';
// Type
import { AuthContextType } from '../../../../hooks/useAuth';

// Data static
import {
  groupLanes,
  groupTracks,
  specialTagType,
} from '../../../../data/orders';

// Compression
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { resolveFreshRegisterDate } from '../../../../utils/freshRegisterDate';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  COMMUNICATION_CREATION_SCOPE,
  communicationCreationOwnerId,
} from '../../../../utils/draftScopes';
import {
  communicationCreationParentPath,
  readCommunicationCreationDraft,
  readOwnCommunicationCreationDraft,
  reconcileCommunicationCreationSelection,
  releaseCommunicationCreationSelection,
  whenReduxRehydrated,
} from '../../../../utils/communicationCreationDraft';

// Store
import { persistor, store } from '../../../../state/configureStore';

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { classifications },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
      ]);

      return {
        ...data,
        capitols,
        predefinedOrders,
        classifications,
        groupTracks,
        groupLanes,
        specialTagType,
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Communication Summary loading error:', err);
    return true;
  }
};

export const NewCommunicationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ request, params }) => {
  // 🔴 Dueño al llegar (K3 de la batería QA del navegador, 2026-11-03; spec
  // 2026-11-02 wizard-draft-ownership): el alta es de la OT/campaña de la URL
  // (`parent:<:id>`) o de la sección suelta (`standalone`, sin `:id`). Con la
  // comunicación de la OT 801 a medias, abrir a mano (o con atrás)
  // `/work-orders/800/new-communication` enseñaba la miga de 801 y la creaba
  // BAJO 801: este loader solo distinguía «árbol de OT ↔ sección suelta», no
  // «OT A ↔ OT B». Síncrono y antes de cualquier `await`: si el borrador es de
  // otro dueño (o de nadie) se descarta. Esto también cubre lo que hacía la
  // comprobación de «mismatch» (borrador de OT al entrar en la sección suelta).
  const parentId = params.id;
  const owner = communicationCreationOwnerId(parentId);
  let previousOwner: string | null = null;
  try {
    previousOwner = sessionStorage.getItem(COMMUNICATION_CREATION_SCOPE.ownerKey);
  } catch {
    previousOwner = null;
  }
  claimDraftScope(COMMUNICATION_CREATION_SCOPE, owner);
  // La OT/campaña elegida en Redux también era del alta anterior (y Redux
  // persiste en `localStorage`): fuera con ella.
  if (previousOwner !== owner) releaseCommunicationCreationSelection(store.dispatch);

  // En los árboles de OT y de campaña, sin alta de ESTE padre no hay
  // asistente: se vuelve a la ficha del padre, cuya «Nueva comunicación» (la
  // puerta) deja el alta, la OT elegida en el modal y la pestaña. Mismo
  // criterio que el alta de accidente (Ruling 9). Antes se pintaba el
  // formulario sin padre (o con el de otra OT) bajo la URL de esta.
  // 🔴 REPLACE, no `redirect`: en React Router 7 la redirección de un loader
  // es un PUSH (también tras un «atrás»), así que la ficha se apilaba encima
  // de esta URL, cortaba el «adelante» y cada «atrás» volvía a caer aquí y a
  // rebotar: no se podía salir (revisión de la Task 12a; botón «atrás» de
  // Android en la APK). Un rebote nunca debe quedar en el historial.
  if (parentId && !readOwnCommunicationCreationDraft(parentId)) {
    logLoader('↩️ sin alta de este padre: a su ficha');
    return replace(
      communicationCreationParentPath(new URL(request.url).pathname, parentId)
    );
  }

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Habilitar logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:newcommunication:*');
  }

  logLoader('🚀 [NewCommunicationLoader] INICIO');

  // Árbol de OT o de campaña (`…/:id/new-communication`) frente a la sección
  // suelta (`/communication`): lo dice el `:id` de la URL.
  const isFromWorkOrder = !!parentId;

  logLoader('📍 parentId:', parentId);
  logLoader('📍 isFromWorkOrder:', isFromWorkOrder);

  // Update context according to current URL
  sessionStorage.setItem(
    formNaming.COMMUNICATION_NAVIGATION_CONTEXT,
    compressSessionStorage(
      JSON.stringify({
        origin: isFromWorkOrder ? 'workorder' : 'communications-section',
      })
    )
  );

  // La OT/campaña elegida en el modal (Redux, persistido en `localStorage`,
  // que comparten las pestañas) solo vale si es la de la URL o la que se
  // eligió A MANO en ESTA alta; la rehidratada de OTRA pestaña se suelta y,
  // bajo una OT, el formulario vuelve a poner la de la URL (revisión de la
  // Task 12a). Se mira TRAS la rehidratación: en una recarga este loader puede
  // correr antes y no la vería.
  await whenReduxRehydrated(persistor);
  reconcileCommunicationCreationSelection(store, parentId);

  // La copia del alta (también tras recargar). En los árboles de OT/campaña,
  // SOLO si es de este padre (el cinturón del reclamo); en la sección suelta,
  // la que haya (su dueño ya es `standalone`).
  const sessionStorageData = isFromWorkOrder
    ? readOwnCommunicationCreationDraft(parentId)
    : readCommunicationCreationDraft();
  const loaderToResolve = await loadFormOptions(userData);
  const nameOperator = `${userData?.user?.user.name} ${
    userData?.user?.user.surName.split(' ')[0]
  }`;

  logLoader('📋 borrador existe:', !!sessionStorageData);

  if (sessionStorageData) {
    // Only include parentOrderId data if we're in the work-orders context
    // This prevents data contamination when navigating directly to Communications section
    const {
      parentOrderId,
      parentOrderRoid,
      ...restSessionData
    } = sessionStorageData;

    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      ...restSessionData,
      operator: nameOperator,
      // Después del borrador, para ganarle: su fecha pudo quedar congelada
      // en una visita anterior (ver `resolveFreshRegisterDate`). Era el caso
      // reportado: suelta → suelta nunca limpia el borrador.
      ...resolveFreshRegisterDate(restSessionData),
      // Only include parent order data if coming from work-orders path
      ...(isFromWorkOrder && typeof parentOrderId === 'string' && parentOrderId && {
        parentOrderId,
        parentOrderRoid,
        breadCrumbsData: {
          parentOrderId,
          parentOrderRoid,
        },
      }),
    };

    logLoader('✅ [NewCommunicationLoader] combinedData (con sessionStorage):');
    logLoader('  - parentOrderId:', combinedData.parentOrderId);
    logLoader('  - isFromWorkOrder:', isFromWorkOrder);

    return combinedData;
  }

  // Component will handle concession pre-selection via useEffect
  const combinedData = {
    // team field removed - users no longer have teamIDs
    ...loaderToResolve,
    operator: nameOperator,
    // También sin borrador: así el marcador existe desde la primera visita.
    ...resolveFreshRegisterDate(undefined),
  };

  logLoader('✅ [NewCommunicationLoader] combinedData (SIN sessionStorage)');

  // Prepare some default values for the form to load properly
  return combinedData;
};

  return { data: load() };
};
